import { DeviceSecurityType, VaultType } from '@ionic-enterprise/identity-vault';
import { Device } from '@ionic-enterprise/identity-vault';
import { BrowserVault, IdentityVaultConfig, Vault } from '@ionic-enterprise/identity-vault';
import { Capacitor } from '@capacitor/core';
import { Ref, ref } from 'vue';

export type UnlockMode = 'Device' | 'SystemPIN' | 'SessionPIN' | 'NeverLock' | 'ForceLogin';
// Splash screen on background prevents iOS login.
Device.setHideScreenOnBackground(false, false);

//
// START Create Vault
const config: IdentityVaultConfig = {
    key: 'com.donatos-pizza.donatos',
    type: VaultType.SecureStorage,
    deviceSecurityType: DeviceSecurityType.None,
    lockAfterBackgrounded: 300000, // 5min
    shouldClearVaultAfterTooManyFailedAttempts: true,
    customPasscodeInvalidUnlockAttempts: 2,
    unlockVaultOnLoad: false,
};
const vault = Capacitor.getPlatform() === 'web' ? new BrowserVault(config) : new Vault(config);
// END Create Vault

// Session
const key = 'sessionData';
const session = ref<string | null | undefined>();

const setSession = async (value: string): Promise<void> => {
    await vault.setValue(key, value);
    session.value = value;
};
const restoreSession = async () => {
    const value = await vault.getValue(key);
    session.value = value;
};
const clearVault = async (): Promise<void> => {
    await vault.clear();
    await setUnlockMode('NeverLock');
    session.value = undefined;
};

//
// START Vault Lock & Unlock methods
const vaultIsLocked = ref(false);
const lockVault = (): Promise<void> => vault.lock();
const unlockVault = (): Promise<void> => vault.unlock();
vault.onLock(() => {
    vaultIsLocked.value = true;
    session.value = undefined;
});

// Do not nest authService methods
// authService unlocks the vault to read data out of it. will cause loop.
vault.onUnlock(() => {
    vaultIsLocked.value = false;
});

const initializeUnlockMode = async () => {
    if (Capacitor.getPlatform() === 'web') return await setUnlockMode('NeverLock');
    // SessionPIN requires an onPasscodeRequested handler and PIN-entry UI that this app does not
    // have, so a device with no screen lock falls back to unlocked keychain/keystore storage.
    if (await Device.isSystemPasscodeSet()) return await setUnlockMode('Device');
    return await setUnlockMode('NeverLock');
};
const canUnlock = async (): Promise<boolean> => !(await vault.isEmpty()) && (await vault.isLocked());

const setUnlockMode = async (unlockMode: UnlockMode): Promise<void> => {
    let type: VaultType;
    let deviceSecurityType: DeviceSecurityType;

    switch (unlockMode) {
        case 'Device':
            type = VaultType.DeviceSecurity;
            deviceSecurityType = DeviceSecurityType.Both;
            break;

        case 'SessionPIN':
            type = VaultType.CustomPasscode;
            deviceSecurityType = DeviceSecurityType.None;
            break;

        case 'ForceLogin':
            type = VaultType.InMemory;
            deviceSecurityType = DeviceSecurityType.None;
            break;

        case 'NeverLock':
            type = VaultType.SecureStorage;
            deviceSecurityType = DeviceSecurityType.None;
            break;

        default:
            type = VaultType.SecureStorage;
            deviceSecurityType = DeviceSecurityType.None;
    }

    await vault.updateConfig({
        ...config,
        ...vault.config,
        type,
        deviceSecurityType,
    });
};
// END Vault Lock & Unlock methods

export interface VaultService {
    vault: Vault | BrowserVault;

    session: Ref<string | null | undefined>;
    setSession: (value: string) => Promise<void>;
    restoreSession: () => Promise<void>;
    clearVault: () => Promise<void>;

    setUnlockMode: (unlockMode: UnlockMode) => Promise<void>;
    initializeUnlockMode: () => Promise<void>;
    canUnlock: () => Promise<boolean>;
    vaultIsLocked: Ref<boolean>;
    lockVault: () => Promise<void>;
    unlockVault: () => Promise<void>;
}

export default (): VaultService => {
    return {
        vault,

        session,
        setSession,
        restoreSession,
        clearVault,

        setUnlockMode,
        initializeUnlockMode,
        canUnlock,
        vaultIsLocked,
        lockVault,
        unlockVault,
    };
};
